import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useRouter } from 'next/router';

import { v1Api } from '@/api/backendApi';
import { logEvent } from '@/pages/api/v1/utils';
import { partnershipKeys } from '@/utils/queryKeys';

import { useHasRole } from '@/hooks/auth';
import { useSettings } from '@/hooks/state/useSettings';
import type { AxiosResponse } from 'axios';
import { useMemo } from 'react';

export interface SubscriptionDetailsReferral {
  /**
   * The percentage that has already been applied.
   *
   * Pro users only.
   */
  current_discount_percentage?: number;
  /**
   * The number of months the discount will be applied for.
   *
   * Pro users only.
   */
  discount_months_after_redemption?: number;
  /** The total amout of discount that will be applied after redeeming. (unredeemed months * 5) + current_discount_percentage */
  discount_percentage_after_redemption?: number;
  /**
   * Return `true` if the user is on a trial.
   *
   * Indicates the Free, Essentials or Creator user has already claimed a referral reward.
   *
   * Does not apply to Pro users.
   */
  in_trial_period?: boolean;
  /**
   * Return `true` if the Free user is on a trial.
   *
   * Indicates the Free user has already claimed a referral reward.
   */
  is_free_trial?: boolean;
  /** ISO date string */
  next_billing_date: string;
  /**
   * ISO date string
   *
   * For Pro users, this will be the same as next_billing_date as we're giving discount instead of free months
   */
  next_billing_date_after_redemption: string;
  /** The next invoice price is the price minus any discount */
  next_invoice_price: number;
  /** Full price of the plan */
  plan_price: number;
  /** This price is what the user should expect to pay after redeeming the extra months. */
  price_after_redemption: number;
  /**
   * The number of months that have been redeemed.
   *
   * The value will be over 1 if the user has already claimed a referral reward.
   *
   * Pro users only.
   */
  redeemed_months_remaining?: number;
  /** The number of months that have been redeemed */
  redeemed_months_total?: number;
}

export interface PartnershipsResponse {
  partnerstackAgreementAccepted: boolean;
  referral_code: string | null;
  referrals: number;
  subscriptionDetails: null | SubscriptionDetailsReferral;
  unlocked_months: number;
  unredeemed_months: number;
}

export const defaultPartnershipsData: PartnershipsResponse = {
  referral_code: null,
  referrals: 0,
  unredeemed_months: 0,
  unlocked_months: 0,
  partnerstackAgreementAccepted: false,
  subscriptionDetails: null,
};

export const usePartnerships = ({
  /**
   * Fetch even when settings reports no referral code. Set on the refer page,
   * whose whole state machine depends on this response.
   */
  alwaysFetch = false,
}: {
  alwaysFetch?: boolean;
} = {}) => {
  const isLoggedIn = useHasRole('User');
  // DEV-6395: getreferrallink is uncached, so only call it for users who are
  // actually on the referral programme. Settings mirrors the referral code,
  // and generatereferrallink writes straight into this query's cache, so a
  // disabled query still picks up freshly generated links.
  const { settings } = useSettings();
  const hasReferralCode = !!settings?.user.referralCode;

  const getPartnershipsDataQuery = useQuery({
    queryKey: partnershipKeys.list(isLoggedIn),
    queryFn: () =>
      v1Api.get<PartnershipsResponse>('admin/user/getreferrallink', {
        headers: { 'X-Skip-CDN': 'true' },
      }),
    select: (response) => {
      // A partner who accepted the agreement but has no code yet must keep
      // their real data, or the account tab renders them as not-joined.
      if (
        response?.data?.referral_code ||
        response?.data?.partnerstackAgreementAccepted
      ) {
        return response.data;
      }
      return defaultPartnershipsData;
    },
    initialData: {
      data: defaultPartnershipsData,
      status: 200,
    } as AxiosResponse<PartnershipsResponse>,
    enabled: isLoggedIn && (alwaysFetch || hasReferralCode),
  });

  const getReferralLinkMutation = useGenerateReferralLink();

  const partnershipsData = useMemo(() => {
    if (
      getPartnershipsDataQuery.data &&
      (getPartnershipsDataQuery.data.referral_code ||
        getPartnershipsDataQuery.data.partnerstackAgreementAccepted ||
        getPartnershipsDataQuery.data.unredeemed_months > 0 ||
        getPartnershipsDataQuery.data.unlocked_months > 0)
    ) {
      return getPartnershipsDataQuery.data;
    }

    return defaultPartnershipsData;
  }, [getPartnershipsDataQuery.data]);

  return {
    partnershipsData,
    getPartnershipsDataQuery,
    getReferralLinkMutation,
  };
};

export const useGenerateReferralLink = () => {
  const queryClient = useQueryClient();
  const router = useRouter();
  const isLoggedIn = useHasRole('User');

  return useMutation({
    mutationFn: () =>
      v1Api.get<PartnershipsResponse>('admin/user/generatereferrallink', {
        headers: { 'X-Skip-CDN': 'true' },
      }),
    onSuccess: (response: AxiosResponse<PartnershipsResponse>) => {
      logEvent({
        event_type: 'referral_link_generated',
        asPath: router.asPath,
        metadata: {
          text: 'Referral link generated',
          referral_code: response.data.referral_code ?? undefined,
        },
      });
      queryClient.setQueryData<AxiosResponse<PartnershipsResponse> | undefined>(
        partnershipKeys.list(isLoggedIn),
        response
      );
    },
    onError: (error) => {
      logEvent({
        event_type: 'error',
        asPath: router.asPath,
        metadata: {
          text: error?.message ?? 'Error generating referral link',
        },
      });
    },
  });
};
